// pages/Voyages.jsx
import React, { useState } from 'react';
import { 
    Plane, Calendar, Filter, 
    Download, Plus, Search,
    MapPin, Clock, ChevronRight,
} from 'lucide-react';
import Sidebar from './SideBar';
import MobileNavigation from './MobileNavigation';
import DashboardHeader from './DashboradHeader';
import useGlobale from '../../components/global';
import menuItems from '../../components/UserMenu';

const VoyageCard = ({ voyage }) => (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all p-6">
        <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#F4F8E9] rounded-lg">
                    <Plane className="w-6 h-6 text-[#437011]" />
                </div>
                <div>
                    <h3 className="font-semibold text-[#437011]">{voyage.from} → {voyage.to}</h3>
                    <p className="text-sm text-gray-600">{voyage.compagnie}</p>
                </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                voyage.status === 'confirmé' 
                    ? 'bg-[#96B734] text-white' 
                    : voyage.status === 'en cours'
                    ? 'bg-[#FF8A3D] text-white'
                    : 'bg-gray-200 text-gray-700'
            }`}>
                {voyage.status}
            </span>
        </div>
        
        <div className="space-y-3">
            <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span className="text-sm">Départ: {voyage.dateDepart}</span>
            </div>
            <div className="flex items-center text-gray-600">
                <Clock className="w-4 h-4 mr-2" />
                <span className="text-sm">Durée: {voyage.duree}</span>
            </div>
            <div className="flex items-center text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                <span className="text-sm">Terminal: {voyage.terminal}</span>
            </div>
        </div>

        <div className="mt-4 pt-4 border-t flex justify-between items-center">
            <div className="text-[#437011]">
                <span className="text-sm">Prix total</span>
                <p className="font-bold">{voyage.prix} FCFA</p>
            </div>
            <button className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#F4F8E9] text-[#437011] hover:bg-[#96B734] hover:text-white transition-colors">
                <span>Voir détails</span>
                <ChevronRight className="w-4 h-4" />
            </button>
        </div>
    </div>
);

const MesVoyages = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { user } = useGlobale();

    const voyages = [
        {
            id: 1,
            from: 'Niamey',
            to: 'Paris',
            compagnie: 'Air France',
            status: 'confirmé',
            dateDepart: '15 Dec 2024',
            duree: '6h 30min',
            terminal: 'Terminal 2E',
            prix: '850,000'
        },
        {
            id: 2,
            from: 'Niamey',
            to: 'Istanbul',
            compagnie: 'Turkish Airlines',
            status: 'en cours',
            dateDepart: '20 Dec 2024',
            duree: '8h 15min',
            terminal: 'Terminal 1',
            prix: '750,000'
        },
        {
            id: 3,
            from: 'Niamey',
            to: 'Dubai',
            compagnie: 'Emirates',
            status: 'terminé',
            dateDepart: '10 Nov 2024',
            duree: '7h 45min',
            terminal: 'Terminal 3',
            prix: '950,000'
        }
    ];

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={menuItems}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1">
                <DashboardHeader
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />

                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
                        <div>
                            <h1 className="text-2xl font-bold text-[#437011] mb-2">Mes Voyages</h1>
                            <p className="text-gray-600">Gérez vos réservations et voyages</p>
                        </div>
                        <div className="mt-4 sm:mt-0">
                            <button className="bg-[#96B734] text-white px-6 py-3 rounded-lg flex items-center space-x-2 hover:bg-[#437011] transition-colors shadow-md hover:shadow-lg">
                                <Plus className="w-5 h-5" />
                                <span>Nouveau Voyage</span>
                            </button>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Rechercher un voyage..."
                                    className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#96B734] bg-[#F4F8E9]"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div>
                                <button 
                                    onClick={() => setFilterOpen(!filterOpen)}
                                    className="w-full sm:w-auto px-6 py-3 border-2 border-[#96B734] text-[#437011] rounded-lg flex items-center justify-center space-x-2 hover:bg-[#F4F8E9] transition-colors"
                                >
                                    <Filter className="w-5 h-5" />
                                    <span>Filtrer</span>
                                </button>
                            </div>
                            <div>
                                <button className="w-full sm:w-auto px-6 py-3 border-2 border-[#96B734] text-[#437011] rounded-lg flex items-center justify-center space-x-2 hover:bg-[#F4F8E9] transition-colors">
                                    <Download className="w-5 h-5" />
                                    <span>Exporter</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Voyages Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {voyages.map((voyage) => (
                            <VoyageCard key={voyage.id} voyage={voyage} />
                        ))}
                    </div>
                </main>

                <MobileNavigation menuItems={menuItems} />
            </div>
        </div>
    );
};

export default MesVoyages;